import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { ToastProvider } from './components/Toast';
import Navbar from './components/Navbar';
import Dashboard from './pages/Dashboard';
import CreateIssue from './pages/CreateIssue';
import IssueDetail from './pages/IssueDetail';
import Tasks from './pages/Tasks';
import CreateTask from './pages/CreateTask';
import TaskDetail from './pages/TaskDetail';
import TaskReports from './pages/TaskReports';
import Login from './pages/Login';
import SignUp from './pages/SignUp';
import VerifyEmail from './pages/VerifyEmail';
import UsersManagement from './pages/UsersManagement';
import Onboarding from './pages/Onboarding';
import ProfilePage from './pages/ProfilePage';
import SettingsPage from './pages/SettingsPage';
import CreateProject from './pages/CreateProject';
import ProjectDetail from './pages/ProjectDetail';
import AcceptInvite from './pages/AcceptInvite';
import AcceptProjectInvite from './pages/AcceptProjectInvite';
import AuthCallback from './pages/AuthCallback';
import ResetPassword from './pages/ResetPassword';
import LandingPage from './pages/LandingPage';
import HelpPage from './pages/HelpPage';
import AboutPage from './pages/AboutPage';
import SuperAdminDashboard from './pages/SuperAdminDashboard';
import { UserRole } from './types';
import './App.css';

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="app-loading">
        <div className="app-loading-spinner" />
        <p>Loading…</p>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  return <>{children}</>;
}

function PublicOnlyRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (user) return <Navigate to="/" replace />;
  return <>{children}</>;
}

function RoleRoute({ children, allowedRoles }: { children: React.ReactNode; allowedRoles: UserRole[] }) {
  const { role, loading } = useAuth();
  if (loading) return null;
  if (!role || !allowedRoles.includes(role)) return <Navigate to="/" replace />;
  return <>{children}</>;
}

function SuperAdminRoute({ children }: { children: React.ReactNode }) {
  const { role, loading } = useAuth();
  if (loading) return null;
  if (role !== 'super_admin') return <Navigate to="/" replace />;
  return <>{children}</>;
}

function OnboardingGate({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  const { profile, company, loading } = useAuth();
  if (loading) return null;
  const missingName = !profile?.full_name?.trim();
  const missingCompany = profile?.role === 'admin' && (!company?.name?.trim() || company.name === 'My Company');
  if ((missingName || missingCompany) && location.pathname !== '/onboarding') {
    return <Navigate to="/onboarding" replace />;
  }
  return <>{children}</>;
}

function HomeRoute() {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="app-loading">
        <div className="app-loading-spinner" />
        <p>Loading…</p>
      </div>
    );
  }
  if (!user) return <LandingPage />;
  return <OnboardingGate><Dashboard /></OnboardingGate>;
}

function AppRoutes() {
  return (
    <div className="app-layout">
      <Navbar />
      <main className="app-main">
        <Routes>
          <Route path="/login" element={<PublicOnlyRoute><Login /></PublicOnlyRoute>} />
          <Route path="/signup" element={<PublicOnlyRoute><SignUp /></PublicOnlyRoute>} />
          <Route path="/accept-invite" element={<AcceptInvite />} />
          <Route path="/auth/callback" element={<AuthCallback />} />
          <Route path="/verify-email" element={<VerifyEmail />} />
          <Route path="/onboarding" element={<ProtectedRoute><Onboarding /></ProtectedRoute>} />
          <Route path="/" element={<HomeRoute />} />
          <Route path="/projects/create" element={<ProtectedRoute><OnboardingGate><CreateProject /></OnboardingGate></ProtectedRoute>} />
          <Route path="/projects/:projectId" element={<ProtectedRoute><OnboardingGate><ProjectDetail /></OnboardingGate></ProtectedRoute>} />
          <Route path="/projects/:projectId/accept-invite" element={<AcceptProjectInvite />} />
          <Route path="/projects/:projectId/issues/create" element={<ProtectedRoute><OnboardingGate><CreateIssue /></OnboardingGate></ProtectedRoute>} />
          <Route path="/issue/:id" element={<ProtectedRoute><OnboardingGate><IssueDetail /></OnboardingGate></ProtectedRoute>} />
          <Route path="/tasks" element={<ProtectedRoute><OnboardingGate><Tasks /></OnboardingGate></ProtectedRoute>} />
          <Route path="/tasks/reports" element={<ProtectedRoute><OnboardingGate><TaskReports /></OnboardingGate></ProtectedRoute>} />
          <Route path="/tasks/create" element={<ProtectedRoute><OnboardingGate><CreateTask /></OnboardingGate></ProtectedRoute>} />
          <Route path="/tasks/:id" element={<ProtectedRoute><OnboardingGate><TaskDetail /></OnboardingGate></ProtectedRoute>} />
          <Route path="/profile" element={<ProtectedRoute><OnboardingGate><ProfilePage /></OnboardingGate></ProtectedRoute>} />
          <Route path="/settings" element={<ProtectedRoute><OnboardingGate><SettingsPage /></OnboardingGate></ProtectedRoute>} />
          <Route
            path="/users"
            element={
              <ProtectedRoute>
                <OnboardingGate>
                  <RoleRoute allowedRoles={['admin']}>
                    <UsersManagement />
                  </RoleRoute>
                </OnboardingGate>
              </ProtectedRoute>
            }
          />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/help"  element={<HelpPage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route
            path="/super-admin"
            element={
              <ProtectedRoute>
                <SuperAdminRoute>
                  <SuperAdminDashboard />
                </SuperAdminRoute>
              </ProtectedRoute>
            }
          />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <ThemeProvider>
        <AuthProvider>
          <ToastProvider>
            <AppRoutes />
          </ToastProvider>
        </AuthProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}

export default App;
